import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import React, { useState } from "react";
import styled from "styled-components";
import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";
import { StyledLink } from "../BuildlingBlocks/StyledLink";
import { DeleteGrayIcon } from "../Icon/DeleteGrayIcon";
import { EditModeBooleanValueEditor } from "./Boolean";

import { IDisplayModeValueEditor, IEditModeValueEditor } from "./Common";
import { EditModeDateValueEditor } from "./Date";

import { FieldNameRender } from "./DesignUI";

import { EditModeNumberValueEditor } from "./Number";
import { EditModeStringValueEditor } from "./String";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

const ObjectListValueRender = ({
    forData,
    length = 15,
    values,
}: {
    values: AppGlueAny[];
    forData: ObjectDataDefinition;
    length?: number;
}) => {
    let requiredLength: boolean = false;
    let renderValue = "";
    let v = values[0] || {};

    renderValue += forData.fields.reduce((s, current, idx) => {
        const fieldValue = typeof v === "object" ? v[current.name] : current.name;
        if (idx === 0) return `${fieldValue}`;
        if (`${s}, ${fieldValue}`.length > length) {
            requiredLength = true;
            return s;
        }
        s = `${s}, ${fieldValue}`;
        return s;
    }, "");

    return (
        <>
            {renderValue} {requiredLength ? `... (${values.length})` : ``}
        </>
    );
};

export const DisplayModeObjectValueEditor = ({ value, forData }: IDisplayModeValueEditor) => {
    return (
        <div
            className="ValueEditor-display"
            data-testid="ValueEditor-Object-Display"
        >
            {Array.isArray(value) ? (
                <ObjectListValueRender
                    forData={forData as ObjectDataDefinition}
                    values={value}
                />
            ) : (
                <>
                    {((forData as ObjectDataDefinition).fields || []).reduce((s, current, idx) => {
                        const fieldValue =
                            value && typeof value === "object" ? Reflect.get(value || {}, current.name) : null;
                        let renderValue = "";
                        if (current.list) {
                            renderValue =
                                fieldValue && Array.isArray(fieldValue) && fieldValue.length
                                    ? `${typeof fieldValue[0] === "object" ? current.name : fieldValue[0]}(${
                                          fieldValue.length
                                      })`
                                    : `(0)`;
                        } else {
                            renderValue = `${typeof fieldValue === "object" ? current.name : fieldValue || ""}`;
                        }
                        if (idx === 0) {
                            return renderValue;
                        }
                        return `${s}, ${renderValue}`;
                    }, "")}
                </>
            )}
        </div>
    );
};

interface IObjectEditModeValueEditor extends IEditModeValueEditor {
    showFieldName?: boolean;
}

export const EditModeObjectValueEditor = React.forwardRef<HTMLElement, IObjectEditModeValueEditor>(
    // eslint-disable-next-line
    ({ value, forData, onUpdate, editMode, showFieldName = true }: IObjectEditModeValueEditor, ref) => {
        if (forData.list) {
            return (
                <ObjectListEditor
                    values={value}
                    updateCallback={onUpdate}
                    forData={forData as ObjectDataDefinition}
                    showFieldName={showFieldName}
                />
            );
        }

        let values = forData.list
            ? Array.isArray(value) && typeof value !== "string"
                ? value
                : value
                  ? [value]
                  : []
            : Array.isArray(value) && typeof value !== "string"
              ? value[0]
              : value;

        const onFieldValueUpdate = (newValue: AppGlueAny, fieldName: string) => {
            const originalValue = typeof values === "object" ? { ...values } : {};
            let newValues = {
                ...originalValue,
                [fieldName]: newValue,
            };
            onUpdate(newValues);
        };

        return (
            <div
                className="ValueEditor-display"
                data-testid="ValueEditor-Object-Edit"
            >
                <>
                    {((forData as ObjectDataDefinition).fields || []).map((field, idx) => (
                        <SubfieldEditorRender key={idx}>
                            {showFieldName && <FieldNameRender>{field.name}</FieldNameRender>}
                            <SubfieldEditor
                                forData={field}
                                value={
                                    values && typeof values === "object" ? Reflect.get(values || {}, field.name) : null
                                }
                                key={idx}
                                showFieldName={showFieldName}
                                onUpdate={(newValue: AppGlueAny) => {
                                    onFieldValueUpdate(newValue, field.name!);
                                }}
                                editMode={editMode}
                            />
                        </SubfieldEditorRender>
                    ))}
                </>
            </div>
        );
    },
);

export interface IObjectListEditor {
    values: AppGlueAny;
    updateCallback: (vals: AppGlueAny[]) => void;
    forData: ObjectDataDefinition;
    showFieldName?: boolean;
}

export const ObjectListEditor: React.FC<IObjectListEditor> = (props: IObjectListEditor) => {
    let values: object[] = [];

    if (Array.isArray(props.values)) {
        values = props.values;
    }

    const [viewDetails, setViewDetails] = useState<{ [key: string]: boolean }>({});

    const onUpdate = (newValue: AppGlueAny, fieldName: string, index: number) => {
        let newValues = [
            ...values.map((v, idx) => {
                if (index !== idx) return v;

                return {
                    ...(typeof v === "object" ? v : {}),
                    [fieldName]: newValue,
                };
            }),
        ];

        props.updateCallback([...newValues]);
    };

    const onViewDetails = (index: number) => {
        setViewDetails({
            ...viewDetails,
            [`v_${index}`]: !viewDetails[`v_${index}`],
        });
    };

    const add = () => {
        props.updateCallback([...values, {}]);
    };

    const remove = (index: number) => {
        let tempValues = [...values];
        tempValues.splice(index, 1);

        props.updateCallback([...tempValues]);
    };

    return (
        <StyledObjectList
            className="PropertyList-Wrapper ObjectList-Wrapper"
            data-testid="ValueEditor-ObjectList-Edit"
        >
            <TableContainer>
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {props.forData.fields.map((f, idx) => {
                                return (
                                    <TableCell key={idx}>
                                        <FieldNameRender className="headerColumn-title">{f.name}</FieldNameRender>
                                    </TableCell>
                                );
                            })}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {values.map((v, idx) => {
                            return (
                                <React.Fragment key={idx}>
                                    <TableRow>
                                        <TableCell>{idx + 1}</TableCell>
                                        {props.forData.fields.map((field, idx2) => (
                                            <TableCell key={idx2}>
                                                <SubfieldEditor
                                                    forData={field}
                                                    value={Reflect.get(v || {}, field.name)}
                                                    onUpdate={(newValue: AppGlueAny) =>
                                                        onUpdate(newValue, field.name, idx)
                                                    }
                                                    editMode="inline"
                                                    subfield={true}
                                                    onViewDetails={() => onViewDetails(idx)}
                                                    show={viewDetails[`v_${idx}`]}
                                                    showFieldName={props.showFieldName}
                                                />
                                            </TableCell>
                                        ))}
                                        <TableCell
                                            onClick={() => remove(idx)}
                                            data-testid={`BtnRemove${props.forData.name}`}
                                        >
                                            <DeleteGrayIcon />
                                        </TableCell>
                                    </TableRow>
                                    {viewDetails[`v_${idx}`] && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={props.forData.fields.length + 2}
                                                classes={{
                                                    root: "ObjectListTableCell-details",
                                                }}
                                            >
                                                {props.forData.fields
                                                    .filter((field) => field.type === DataTypes.OBJECT)
                                                    .map((field, index) => (
                                                        <React.Fragment key={index}>
                                                            <FieldNameRender className="ValueEditor-fieldLabel">
                                                                {field.name}
                                                            </FieldNameRender>
                                                            <SubfieldEditor
                                                                forData={field}
                                                                value={Reflect.get(v || {}, field.name)}
                                                                onUpdate={(newValue: AppGlueAny) =>
                                                                    onUpdate(newValue, field.name, idx)
                                                                }
                                                                showFieldName={props.showFieldName}
                                                                editMode="inline"
                                                            />
                                                        </React.Fragment>
                                                    ))}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        <TableRow>
                            <TableCell colSpan={props.forData.fields.length + 2}>
                                <StyledLink
                                    onClick={add}
                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                    classes={{ root: "PropertyList-Add" }}
                                    data-testid={`BtnAdd${props.forData.name}`}
                                >
                                    Add
                                </StyledLink>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </StyledObjectList>
    );
};

interface ISubfieldEditor extends IObjectEditModeValueEditor {
    subfield?: boolean;
    onViewDetails?: () => void;
    show?: boolean;
}

const SubfieldEditor = React.forwardRef<HTMLElement, ISubfieldEditor>((props: ISubfieldEditor, ref) => {
    switch (props.forData.type) {
        case DataTypes.BOOLEAN:
            return (
                <EditModeBooleanValueEditor
                    {...props}
                    ref={ref}
                />
            );
        case DataTypes.STRING:
            return (
                <EditModeStringValueEditor
                    {...props}
                    ref={ref}
                />
            );
        case DataTypes.DATE:
            return (
                <EditModeDateValueEditor
                    {...props}
                    ref={ref}
                />
            );
        case DataTypes.NUMBER:
            return (
                <EditModeNumberValueEditor
                    {...props}
                    ref={ref}
                />
            );
        case DataTypes.FILE:
            return null;
        case DataTypes.OBJECT:
            return props.subfield ? (
                <ViewDetailsButton onClick={props.onViewDetails}>
                    {!props.show ? "show details" : "hide details"}
                </ViewDetailsButton>
            ) : (
                <EditModeObjectValueEditor
                    {...props}
                    ref={ref}
                />
            );
        default:
            return null;
    }
});

const StyledObjectList = styled(Paper)`
    min-width: 600px;
    overflow: auto;

    text-align: right;
    margin: 20px 0;

    .ObjectListTableCell-details {
        padding: 20px;
    }

    .headerColumn-title {
        justify-content: center;
    }
`;

const ViewDetailsButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SubfieldEditorRender = styled.div`
    padding: 5px 0;
`;
